import React, { useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'

import BreakPopover from './BreakPopover';

import '../styles/fullCalendar.css';

const eventToBreakHour = (event) => {
  const start = moment(event.start);
  const end = moment(event.end)
  const start_hr = start.hours() + parseFloat((start.minutes() / 60).toFixed(2));
  const end_hr = end.hours() + parseFloat((end.minutes() / 60).toFixed(2));
  const workday = start.day() === 0 ? 7 : start.day();
  return {
    start_hr,
    end_hr,
    workday,
  };
};

const momentToHours = (m) => {
  const minutes=parseFloat((m.minutes() / 60).toFixed(2))
  return m.hours() + (minutes!==0.98 ? parseFloat((m.minutes() / 60).toFixed(2)):0.99)
};

const ScheduleCalendar = ({
  schedule,
  shifts,
  setShifts,
  breakBgColor,
  borderRadius,
  dayShiftBgColor,
  nightShiftBgColor,
  ...calendarConfig
}) => {
  const [currentBreak, setCurrentBreak] = useState(null);
  const oldEventRef = useRef(null);
  const calendarProps = useMemo(() => {
    return { ...defaultCalendarConfig, ...calendarConfig };
  }, [calendarConfig]);

  const addBreakHour = (breakHour) => {
    const shift = shifts.find(shift => {
      let found = false;
      const shiftHours = shift.workHours.find(hours => hours.workday === breakHour.workday);

      if (shiftHours) {
        if (shiftHours.start_hr > shiftHours.end_hr) {
          found = breakHour.start_hr >= shiftHours.start_hr && breakHour.end_hr <= shiftHours.end_hr + 24;
        } else {
          found = breakHour.start_hr >= shiftHours.start_hr && breakHour.end_hr <= shiftHours.end_hr;
        }
      }

      const shiftHoursPrevDay = shift.workHours.find(hours => hours.workday === (breakHour.workday !== 1 ? breakHour.workday - 1 : 7));
      if (!found && shiftHoursPrevDay) {
        if (shiftHoursPrevDay.start_hr > shiftHoursPrevDay.end_hr) {
          found = breakHour.end_hr <= shiftHoursPrevDay.end_hr;
        }
      }

      return found;
    });

    if (shift && typeof setShifts === 'function') {
      shift.breakHours.push(breakHour);
      setShifts([...shifts]);
    }

    return shift !== undefined;
  };

  const updateCurrentBreakHour = (breakHour) => {
    const { shift_index, break_index } = currentBreak;
    if (!shifts[shift_index]) return;
    if (!shifts[shift_index].breakHours[break_index]) return;

    const breakHours = shifts[shift_index].breakHours.map((h, i) => {
      if (i === break_index) {
        return Object.assign({}, h, breakHour);
      }
      return h;
    });

    if (typeof setShifts === 'function') {
      shifts[shift_index].breakHours = breakHours;
      setShifts([...shifts]);
    }
  };

  const removeCurrentBreakHour = () => {
    const { shift_index, break_index } = currentBreak;
    if (!shifts[shift_index]) return;
    if (!shifts[shift_index].breakHours[break_index]) return;

    const breakHours = shifts[shift_index].breakHours.filter((h, i) => {
      return i !== break_index;
    });

    if (typeof setShifts === 'function') {
      shifts[shift_index].breakHours = breakHours;
      setShifts([...shifts]);
    }
  };

  const events = useMemo(() => {
    if (shifts) {
      const eventOptions = {
        breakBgColor,
        borderRadius,
        dayShiftBgColor,
        nightShiftBgColor,
      };

      let eventId = 1;
      const eventsData = [];
      shifts.forEach((shift, shiftIndex) => {
        shift.workHours.forEach(hour => {
          if (hour.start_hr > hour.end_hr) {
            eventsData.push({
              ...hour,
              end_hr: 23.99,
              groupId: 'shifts',
              title: `Shift #${shift.shift_name}`,
              type: 'shift',
              display: 'background',
            });
            eventsData.push({
              ...hour,
              start_hr: 0,
              workday: hour.workday + 1,
              groupId: 'shifts',
              title: `Shift #${shift.shift_name}`,
              type: 'shift',
              display: 'background',
            });
          } else {
            eventsData.push({
              ...hour,
              groupId: 'shifts',
              title: `Shift #${shift.shift_name}`,
              type: 'shift',
              display: 'background',
            });
          }
        });

        shift.breakHours.forEach((shiftBreak, breakIndex) => {
          eventsData.push({
            ...shiftBreak,
            id: eventId++,
            groupId: 'breaks',
            shift_index: shiftIndex,
            break_index: breakIndex,
            constraint: 'shifts',
            type: 'break',
            display: 'list-item',
          });
        });
      });

      const events = eventsData.map(event => createEvent(event, eventOptions));
      return events;
    }

    return [];
  }, [shifts, breakBgColor, borderRadius, dayShiftBgColor, nightShiftBgColor]);

  return (
    <>
      <FullCalendar
        {...calendarProps}
        eventBackgroundColor={breakBgColor}
        nextDayThreshold="00:00:00"
        selectConstraint="shifts"
        eventContent={info => {
          if (calendarProps.editable && currentBreak && info.event.extendedProps) {
            if (currentBreak.id === info.event.extendedProps.id) {
              return (
                <BreakPopover
                  visible={true}
                  breakHour={currentBreak}
                  onSave={breakTime => {
                    updateCurrentBreakHour({
                      start_hr: momentToHours(breakTime.start),
                      end_hr: momentToHours(breakTime.end)
                    });
                    setCurrentBreak(null);
                    oldEventRef.current = null;
                  }}
                  onCancel={() => {
                    setCurrentBreak(null);
                    if (oldEventRef.current !== null) {
                      oldEventRef.current.revert();
                      oldEventRef.current = null;
                    }
                  }}
                  onDelete={() => {
                    removeCurrentBreakHour();
                    setCurrentBreak(null);
                    oldEventRef.current = null;
                  }}
                />
              );
            }
          }
        }}
        eventClick={eventInfo => {
          if (eventInfo.event.groupId === 'breaks') {
            setCurrentBreak(eventInfo.event.extendedProps);
          }
        }}
        eventDrop={eventInfo => {
          oldEventRef.current = eventInfo;
          setCurrentBreak({
            ...eventInfo.event.extendedProps,
            ...eventToBreakHour(eventInfo.event)
          });
        }}
        eventResize={eventInfo => {
          oldEventRef.current = eventInfo;
          setCurrentBreak({
            ...eventInfo.event.extendedProps,
            ...eventToBreakHour(eventInfo.event)
          });
        }}
        select={eventInfo => {
          const breakHour = eventToBreakHour(eventInfo);
          addBreakHour(breakHour);
        }}
        events={events}
      />
    </>
  );
};

const defaultCalendarConfig = {
  plugins: [timeGridPlugin, interactionPlugin],
  initialView: 'timeGridWeek',
  dayHeaderFormat: { weekday: 'long' },
  firstDay: 1,
  allDaySlot: false,
  navLinks: false,
  headerToolbar: false,
  editable: true,
  selectable: true,
  selectMirror: true,
  weekends: true,
  nowIndicator: false,
  contentHeight: 'auto',
  slotDuration: '00:30:00',
  slotLabelInterval: '01:00:00',
  eventMinHeight: 20,
};

const createEvent = (data, options = {}) => {
  const {
    borderRadius,
    dayShiftBgColor,
    nightShiftBgColor,
    breakBgColor,
  } = options;

  const eventProps = Object.assign({}, {
    id: data.id,
    constraint: data.constraint,
    title: data.title,
    groupId: data.groupId,
  });
  // remove undefined fields from eventProps
  Object.keys(eventProps).forEach(key => {
    if (eventProps[key] === undefined)
      delete eventProps[key];
  });

  if (data.type === 'shift') {
    // how many day hours (6am to 6pm) / night hours
    const dayHours = Math.min(data.end_hr, 18) - Math.max(Math.abs(data.start_hr - 6), 0);
    const nightHours = Math.max(Math.abs(data.end_hr - 18), 0) + Math.min(data.start_hr, 6);
    const isNightShift = nightHours > dayHours;

    eventProps.backgroundColor = isNightShift ? nightShiftBgColor : dayShiftBgColor;
    eventProps.display = data.display || 'background';
    eventProps.editable = false;
  } else if (data.type === 'break') {
    eventProps.backgroundColor = breakBgColor;
  }

  const startTime = moment(data.start_hr * 3600 * 1000).utc().format('HH:mm');
  const endTime = moment(data.end_hr * 3600 * 1000).utc().format('HH:mm');

  // the calendar considers 0 as sunday
  const daysOfWeek = (Array.isArray(data.workday)
    ? data.workday
    : [data.workday]
  ).map(dayOfWeek => dayOfWeek % 7);
  
  return {
    ...eventProps,
    borderRadius,
    // <info>
    startTime,
    endTime,
    daysOfWeek,
    isMultipleDay: daysOfWeek.length > 1,
    // <info/>
    extendedProps: data,
  };
};

ScheduleCalendar.propTypes = {
  borderRadius: PropTypes.number,
  dayShiftBgColor: PropTypes.string,
  nightShiftBgColor: PropTypes.string,
  breakBgColor: PropTypes.string,
}

ScheduleCalendar.defaultProps = {
  borderRadius: 4,
  dayShiftBgColor: '#42B0D5',
  nightShiftBgColor: '#1D678A',
  breakBgColor: '#FA8C16',
};

export default ScheduleCalendar;
